'use client';

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

/**
 * Theme type - supports light and dark modes
 */
export type Theme = 'light' | 'dark';

/**
 * Context value provided by ThemeProvider
 */
export interface ThemeContextValue {
  /** Current theme */
  theme: Theme;
  /** Resolved theme (same as theme since we don't support 'system') */
  resolvedTheme: Theme;
  /** Set the theme explicitly */
  setTheme: (theme: Theme) => void;
  /** Toggle between light and dark themes */
  toggleTheme: () => void;
}

/**
 * Props for the ThemeProvider component
 */
export interface ThemeProviderProps {
  /** Child components */
  children: ReactNode;
  /** Default theme to use when no stored value exists (default: 'light') */
  defaultTheme?: Theme;
  /** localStorage key for persisting theme (default: 'theme') */
  storageKey?: string;
  /** Document to theme when rendering inside an iframe or embedded document */
  targetDocument?: Document;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function getBrowserWindow(targetDocument?: Document): Window | undefined {
  return (
    targetDocument?.defaultView ??
    (typeof window === 'undefined' ? undefined : window)
  );
}

function getBrowserDocument(targetDocument?: Document): Document | undefined {
  return (
    targetDocument ?? (typeof document === 'undefined' ? undefined : document)
  );
}

function readStoredTheme(
  storageKey: string,
  targetDocument?: Document,
): string | null {
  try {
    return (
      getBrowserWindow(targetDocument)?.localStorage.getItem(storageKey) ?? null
    );
  } catch {
    return null;
  }
}

function writeStoredTheme(
  storageKey: string,
  theme: Theme,
  targetDocument?: Document,
): void {
  try {
    getBrowserWindow(targetDocument)?.localStorage.setItem(storageKey, theme);
  } catch {
    // Storage can be unavailable in private contexts or embedded runtimes.
  }
}

/**
 * Get the initial theme from localStorage or return default
 * Returns defaultTheme during SSR (no localStorage available)
 */
function getInitialTheme(
  storageKey: string,
  defaultTheme: Theme,
  targetDocument?: Document,
): Theme {
  const stored = readStoredTheme(storageKey, targetDocument);
  if (stored === 'light' || stored === 'dark') {
    return stored;
  }

  return defaultTheme;
}

/**
 * Update the DOM to reflect the current theme
 */
function updateDOM(theme: Theme, targetDocument?: Document): void {
  const root = getBrowserDocument(targetDocument)?.documentElement;
  if (!root) {
    return;
  }

  root.classList.remove('light', 'dark');
  root.classList.add(theme);
}

/**
 * ThemeProvider component
 *
 * Provides theme context to the application with support for:
 * - Persistent theme storage via localStorage
 * - DOM class management for CSS-based theming
 * - Cross-tab synchronization via storage events
 *
 * @llms Theme context provider with localStorage persistence, cross-tab sync, light/dark modes
 * @example
 * ```tsx
 * <ThemeProvider defaultTheme="dark" storageKey="my-app-theme">
 *   <App />
 * </ThemeProvider>
 * ```
 */
export function ThemeProvider({
  children,
  defaultTheme = 'light',
  storageKey = 'theme',
  targetDocument,
}: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(() =>
    getInitialTheme(storageKey, defaultTheme, targetDocument),
  );

  const setTheme = useCallback(
    (newTheme: Theme) => {
      setThemeState(newTheme);
      writeStoredTheme(storageKey, newTheme, targetDocument);
      updateDOM(newTheme, targetDocument);
    },
    [storageKey, targetDocument],
  );

  const toggleTheme = useCallback(() => {
    setTheme(theme === 'light' ? 'dark' : 'light');
  }, [theme, setTheme]);

  // Sync DOM class on mount (for SSR hydration)
  useEffect(() => {
    updateDOM(theme, targetDocument);
  }, [theme, targetDocument]);

  // Listen for storage events (cross-tab sync)
  useEffect(() => {
    const browserWindow = getBrowserWindow(targetDocument);
    if (!browserWindow) {
      return;
    }

    const handleStorage = (event: StorageEvent) => {
      if (event.key === storageKey && event.newValue) {
        const newTheme = event.newValue as Theme;
        if (newTheme === 'light' || newTheme === 'dark') {
          setThemeState(newTheme);
          updateDOM(newTheme, targetDocument);
        }
      }
    };

    browserWindow.addEventListener('storage', handleStorage);
    return () => browserWindow.removeEventListener('storage', handleStorage);
  }, [storageKey, targetDocument]);

  const value: ThemeContextValue = {
    theme,
    resolvedTheme: theme, // Same as theme since we don't support 'system'
    setTheme,
    toggleTheme,
  };

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

/**
 * Hook to access the theme context
 *
 * @throws Error if used outside of ThemeProvider
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { theme, setTheme, toggleTheme } = useTheme();
 *
 *   return (
 *     <button onClick={toggleTheme}>
 *       Current theme: {theme}
 *     </button>
 *   );
 * }
 * ```
 */
export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);

  if (context === null) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }

  return context;
}

/**
 * Hook to optionally access the theme context without throwing
 *
 * Returns null if used outside of ThemeProvider, making it safe for
 * components that support both controlled and uncontrolled modes.
 *
 * @example
 * ```tsx
 * function MyComponent({ value, onValueChange }) {
 *   const themeContext = useThemeOptional();
 *   const isControlled = value !== undefined;
 *
 *   // Use controlled value if provided, otherwise context
 *   const theme = isControlled ? value : themeContext?.theme;
 * }
 * ```
 */
export function useThemeOptional(): ThemeContextValue | null {
  return useContext(ThemeContext);
}
